import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Clock, ChefHat, Package, KeyRound, Star, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import { apiFetch, getSessionToken } from '../lib/apiClient';
import { formatOrderNumberLabel } from '../lib/orderUtils';
import { getOrderReadiness } from '../lib/orderStatus';
import OrderComplaintSection from '../components/OrderComplaintSection';
import { notify, ORDER_UPDATES_CHANNEL } from '../lib/notifications';
import TruckLocationMap from '../components/TruckLocationMap';
import { hasExactLocation } from '../lib/truckLocation';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const STATUS_CONFIG = {
  pending_payment: { label: 'PENDING PAYMENT', color: 'text-yellow-400', icon: Clock },
  pending_vendor_approval: { label: 'WAITING FOR VENDOR', color: 'text-yellow-400', icon: ChefHat },
  confirmed: { label: 'CONFIRMED', color: 'text-green-400', icon: CheckCircle2 },
  preparing: { label: 'PREPARING', color: 'text-yellow-400', icon: ChefHat },
  ready: { label: 'READY FOR PICKUP', color: 'text-green-400 animate-pulse', icon: Package },
  picked_up: { label: 'PICKED UP', color: 'text-muted-foreground', icon: CheckCircle2 },
  cancelled: { label: 'CANCELLED', color: 'text-red-400', icon: Clock }
};

export default function OrderTracking() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundLoading, setRefundLoading] = useState(false);
  const [reviewExists, setReviewExists] = useState(null); // null=unknown, true/false known
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastStatusRef = useRef(null);
  const [truckLocation, setTruckLocation] = useState(null);
  const orderTruckId = order?.truck_id;

  useEffect(() => {
    if (!orderTruckId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/trucks/${orderTruckId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((truck) => {
        if (!cancelled && hasExactLocation(truck?.exact_location)) setTruckLocation(truck.exact_location);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orderTruckId]);

  // Check payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      const checkPayment = async () => {
        try {
          let attempts = 0;
          const maxAttempts = 5;
          
          const poll = async () => {
            const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, {
              credentials: 'include'
            });
            const data = await response.json();
            
            if (data.payment_status === 'paid') {
              toast.success('Payment successful! Your order is confirmed.');
              fetchOrder();
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            }
          };
          
          poll();
        } catch (error) {
          console.error('Payment check error:', error);
        }
      };
      checkPayment();
    }
  }, [searchParams]);

  // Status alerts run from one place with the full order, so a change seen first by
  // polling or by the socket is announced exactly once.
  const announceStatusChange = (next) => {
    const previous = lastStatusRef.current;
    lastStatusRef.current = next.status;
    if (!previous || previous === next.status) return;

    if (next.status === 'confirmed') {
      toast.success('Your order was confirmed');
      notify({
        title: 'Order confirmed',
        body: 'The vendor accepted your order.',
        sound: true,
        channelId: ORDER_UPDATES_CHANNEL,
        playInAppSound: true,
      });
    } else if (next.status === 'preparing') {
      toast.success('Your order is being prepared');
      notify({
        title: 'Order being prepared',
        body: 'The vendor has started on your order.',
        sound: true,
        channelId: ORDER_UPDATES_CHANNEL,
        playInAppSound: true,
      });
    } else if (next.status === 'ready') {
      toast.success('Your order is ready for pickup!');
      notify({
        title: 'Order ready for pickup',
        body: 'Head over and collect your order.',
        sound: true,
        channelId: ORDER_UPDATES_CHANNEL,
        playInAppSound: true,
      });
    } else if (next.status === 'cancelled') {
      let refundText = 'Your card was not charged.';
      if (next.refund_id) refundText = 'A refund has been issued to your card.';
      else if (next.payment_method === 'wallet') refundText = 'The funds were returned to your wallet.';
      toast.error('Your order was cancelled');
      notify({
        title: 'Order cancelled',
        body: refundText,
        sound: true,
        channelId: ORDER_UPDATES_CHANNEL,
        playInAppSound: true,
      });
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await apiFetch(`/api/orders/${orderId}`);
      if (!response.ok) throw new Error('Order not found');
      const data = await response.json();
      announceStatusChange(data);
      setOrder(data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentRefund = async () => {
    if (!order) return;
    if (refundLoading) return;

    try {
      setRefundLoading(true);
      const response = await apiFetch(`/api/orders/${order.order_id}/refund`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Refund request failed');
      toast.success('Refund request submitted');
      fetchOrder();
    } catch (error) {
      console.error('Refund request error:', error);
      toast.error(error.message || 'Refund request failed');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleStudentCancelBeforeAccept = async () => {
    if (!order || order.status !== 'pending_vendor_approval') return;
    setCancellingOrder(true);
    try {
      const response = await apiFetch(`/api/orders/${order.order_id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Cancelled by student before vendor acceptance' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not cancel order');
      toast.success('Order cancelled');
      fetchOrder();
    } catch (error) {
      toast.error(error.message || 'Could not cancel order');
    } finally {
      setCancellingOrder(false);
    }
  };

  // Once the order is picked up, check whether the student has already reviewed it.
  useEffect(() => {
    let cancelled = false;
    const checkReview = async () => {
      if (order?.status !== 'picked_up') return;
      try {
        const response = await apiFetch(`/api/orders/${orderId}/review`);
        if (!cancelled) setReviewExists(response.ok);
      } catch {
        if (!cancelled) setReviewExists(false);
      }
    };
    checkReview();
    return () => { cancelled = true; };
  }, [order?.status, orderId]);

  const submitReview = async () => {
    if (reviewRating < 1) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmittingReview(true);
    try {
      const response = await apiFetch(`/api/orders/${orderId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: reviewRating, review_text: reviewText.trim() || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Could not submit review');
      }
      toast.success('Thanks for your review!');
      setReviewExists(true);
    } catch (error) {
      toast.error(error.message || 'Could not submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    fetchOrder();

    let disposed = false;
    let reconnectTimer = null;
    let ws = null;

    // Set up WebSocket for real-time updates
    const connectWS = () => {
      if (disposed) return;
      try {
        const token = getSessionToken();
        ws = new WebSocket(`${WS_URL}/ws/orders/${orderId}?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          let data;
          try {
            data = JSON.parse(event.data);
          } catch {
            return;
          }
          if (data.status) {
            setOrder(prev => (prev ? { ...prev, status: data.status } : prev));
            // Refetch for pickup code / refund details; the alert fires from fetchOrder.
            fetchOrder();
          }
        };

        ws.onerror = () => {
          // Polling remains the fallback; avoid noisy console on Safari.
        };

        ws.onclose = () => {
          if (disposed) return;
          reconnectTimer = setTimeout(connectWS, 5000);
        };
      } catch (error) {
        if (!disposed) {
          reconnectTimer = setTimeout(connectWS, 5000);
        }
      }
    };

    connectWS();

    // Fallback polling every 10 seconds
    pollIntervalRef.current = setInterval(fetchOrder, 10000);

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      }
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [orderId]);

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Toronto'
    });
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-lg mx-auto px-4 pt-24">
          <div className="bg-[#111] rounded-2xl p-6 animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/2 mb-4" />
            <div className="h-16 bg-gray-800 rounded mb-4" />
            <div className="h-4 bg-gray-800 rounded w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-lg mx-auto px-4 pt-24 text-center">
          <h1 className="text-2xl font-bold mb-4">Order not found</h1>
          <Link to="/orders">
            <Button>Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending_payment;
  const readiness = getOrderReadiness(order);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      
      <div className="max-w-lg mx-auto px-4 pt-20">
        <Link
          to="/orders"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Orders
        </Link>

        {/* Flight Departure Board Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111] rounded-2xl overflow-hidden shadow-2xl"
          data-testid="order-tracking-board"
        >
          {/* Header */}
          <div className="bg-[#1a1a1a] px-6 py-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm font-mono">ORDER STATUS</span>
              <span className="text-gray-500 text-xs font-mono">
                {formatDate(order.created_at)}
              </span>
            </div>
          </div>

          {/* Status Display */}
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <StatusIcon className={`w-8 h-8 ${statusConfig.color}`} />
              <div className="font-mono">
                <div className={`text-2xl font-bold ${statusConfig.color}`} data-testid="order-status">
                  {readiness.label}
                </div>
                <div className="text-gray-500 text-sm">
                  Order {formatOrderNumberLabel(order)} · {readiness.detail}
                </div>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8">
              {['pending_vendor_approval', 'confirmed', 'ready', 'picked_up'].map((step, i) => {
                const steps = ['pending_vendor_approval', 'confirmed', 'ready', 'picked_up'];
                const currentStatus = order.status === 'preparing' ? 'confirmed' : order.status;
                const currentIndex = steps.indexOf(currentStatus);
                const stepIndex = steps.indexOf(step);
                const isActive = stepIndex <= currentIndex;
                const isCurrent = step === currentStatus;
                
                return (
                  <div key={step} className="flex items-center">
                    <div className={`
                      w-3 h-3 rounded-full 
                      ${isActive ? 'bg-green-400' : 'bg-gray-700'}
                      ${isCurrent ? 'ring-2 ring-green-400/50 ring-offset-2 ring-offset-[#111]' : ''}
                    `} />
                    {i < steps.length - 1 && (
                      <div className={`
                        w-16 sm:w-24 h-0.5 
                        ${stepIndex < currentIndex ? 'bg-green-400' : 'bg-gray-700'}
                      `} />
                    )}
                  </div>
                );
              })}
            </div>

            {order.status === 'cancelled' && !order.refund_id && (
              <div className="mb-6">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleStudentRefund}
                  disabled={refundLoading}
                  data-testid="request-refund-btn"
                >
                  {refundLoading ? 'Processing...' : 'Cancel & Refund'}
                </Button>
              </div>
            )}

            {order.status === 'pending_vendor_approval' && (
              <div className="mb-6 space-y-3">
                <p className="text-xs text-gray-400 text-center">
                  Payment authorized — you are charged only if the vendor accepts.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleStudentCancelBeforeAccept}
                  disabled={cancellingOrder}
                >
                  {cancellingOrder ? 'Cancelling…' : 'Cancel order (before vendor accepts)'}
                </Button>
              </div>
            )}

            {/* Order Details */}
            <div className="space-y-4 font-mono text-sm">
              <div className="flex justify-between items-center py-2 border-b border-gray-800">
                <span className="text-gray-400">PICKUP</span>
                <span className="text-white">{formatTime(order.scheduled_pickup_time)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-800">
                <span className="text-gray-400">TRUCK</span>
                <span className="text-white">{order.truck_name}</span>
              </div>
              {Number(order.skip_the_line_fee || 0) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-800" data-testid="receipt-skip-the-line">
                  <span className="text-gray-400 inline-flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5 text-primary fill-current" />
                    SKIP THE LINE
                  </span>
                  <span className="text-white">CA$ {Number(order.skip_the_line_fee).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 border-b border-gray-800">
                <span className="text-gray-400">TOTAL</span>
                <span className="text-green-400">CA$ {order.total_amount.toFixed(2)}</span>
              </div>
            </div>

            {/* Items */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <span className="text-gray-400 text-sm font-mono block mb-3">ITEMS</span>
              <div className="space-y-2">
                {order.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm font-mono">
                    <span className="text-gray-300">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="text-gray-500">CA$ {item.subtotal.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pickup code */}
        {order.status === 'ready' && order.pickup_code && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-[#111] border border-green-500/30 rounded-2xl text-center shadow-lg"
            data-testid="pickup-code-card"
          >
            <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
              <KeyRound className="w-5 h-5" />
              <span className="text-sm font-mono uppercase tracking-wider">Your pickup code</span>
            </div>
            <p
              className="text-4xl sm:text-5xl font-bold font-mono tracking-[0.35em] text-white mb-3"
              data-testid="pickup-code-value"
            >
              {order.pickup_code}
            </p>
            <p className="text-sm text-gray-400">
              Tell the vendor at {order.truck_name} this code when you collect your order.
            </p>
          </motion.div>
        )}

        {/* Instructions */}
        {order.status === 'ready' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-center"
          >
            <p className="text-green-600 font-medium">
              Head to {order.truck_name} to pick up your order!
            </p>
          </motion.div>
        )}

        {truckLocation && !['picked_up', 'cancelled', 'pending_payment'].includes(order.status) && (
          <div className="mt-4 p-4 bg-card border rounded-xl space-y-2" data-testid="order-truck-location">
            <p className="font-medium">Where to find {order.truck_name}</p>
            {truckLocation.note && <p className="text-sm text-muted-foreground">{truckLocation.note}</p>}
            <TruckLocationMap location={truckLocation} className="h-40" />
          </div>
        )}

        <OrderComplaintSection order={order} onChanged={fetchOrder} />

        {/* Rate & review (after pickup, one per order) */}
        {order.status === 'picked_up' && reviewExists === false && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-5 bg-card border rounded-2xl shadow-sm"
            data-testid="review-card"
          >
            <h3 className="font-semibold mb-1">How was {order.truck_name}?</h3>
            <p className="text-sm text-muted-foreground mb-3">Rate your order and leave an optional review.</p>
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setReviewRating(star)}
                  onMouseEnter={() => setReviewHover(star)}
                  onMouseLeave={() => setReviewHover(0)}
                  className="p-1"
                  data-testid={`review-star-${star}`}
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <Star
                    className={`w-7 h-7 ${
                      (reviewHover || reviewRating) >= star
                        ? 'fill-primary text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Tell others about the food (optional)"
              rows={3}
              maxLength={1000}
              className="w-full p-3 rounded-xl bg-muted border-0 resize-none text-base mb-3"
              data-testid="review-text"
            />
            <Button
              className="w-full rounded-xl"
              onClick={submitReview}
              disabled={submittingReview}
              data-testid="submit-review-btn"
            >
              {submittingReview ? 'Submitting…' : 'Submit review'}
            </Button>
          </motion.div>
        )}

        {order.status === 'picked_up' && reviewExists === true && (
          <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl text-center">
            <p className="text-sm text-muted-foreground">Thanks for reviewing this order!</p>
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
}
